import type { AxiosRequestConfig } from "axios";
import axios from "axios";

import { medplum } from "./medplum";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

function getAuthHeaders(): HeadersInit {
  const token = medplum.getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Fetch wrapper with auth and token refresh (for streaming endpoints) */
async function apiFetch(path: string, init?: RequestInit, isRetry = false): Promise<Response> {
  await medplum.refreshIfExpired();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      ...init?.headers,
      ...getAuthHeaders(),
    },
  });

  if (response.status === 401 && !isRetry) {
    await medplum.refreshIfExpired();
    return apiFetch(path, init, true);
  }

  return response;
}

client.interceptors.request.use((config) => {
  const token = medplum.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await medplum.refreshIfExpired();
        const token = medplum.getAccessToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return client(originalRequest);
        }
      } catch {
        // Refresh failed, onUnauthenticated will redirect
      }
    }

    return Promise.reject(error);
  },
);

/** Unified API client with auth and token refresh */
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) => client.get<T>(url, config).then((r) => r.data),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    client.post<T>(url, data, config).then((r) => r.data),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    client.patch<T>(url, data, config).then((r) => r.data),
  delete: <T>(url: string, config?: AxiosRequestConfig) => client.delete<T>(url, config).then((r) => r.data),
  /** For SSE/streaming endpoints */
  stream: apiFetch,
};
